/**
 * Marks — the drawn half of the status language.
 *
 * A status in this product is never carried by colour alone. That is partly
 * WCAG 2.2 AA, and partly the room: laboratory monitors are colour-calibrated
 * for nothing in particular, printed screenshots end up in files, and roughly
 * one man in twelve cannot separate the in-tolerance green from the
 * out-of-tolerance red. So every state carries three signals at once — a drawn
 * mark, a translated word, and a colour — and any one of them is enough.
 *
 * The set is authored rather than imported. It is small, it has to sit at 14px
 * inside a chip beside 11px label text, and it has to hold one visual grammar
 * with the rest of the sheet: a 16-unit grid, a 1.5 stroke, butt caps and
 * mitred joins, drawn the way a drafting pen draws. A general icon library
 * brings a rounder, softer hand and 900 icons nobody needs on the wire.
 *
 * Every mark is `aria-hidden`. The word beside it is the accessible name; a
 * screen reader announcing "check mark, approved" is reading the drawing out
 * loud, which helps no one.
 */

export interface MarkProps {
  /** Edge length in pixels. Marks are square. */
  readonly size?: number;
  readonly className?: string;
}

function Mark({
  size = 14,
  className,
  children,
}: MarkProps & { readonly children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="butt"
      strokeLinejoin="miter"
      aria-hidden="true"
      focusable="false"
      className={className}
      /* Marks sit on a text baseline beside a word; without this they ride
       * high and the chip looks misaligned at every size. */
      style={{ flex: 'none', verticalAlign: '-0.125em' }}
    >
      {children}
    </svg>
  );
}

/** Inside the limits, or a workflow step that completed. */
export function MarkInTolerance(props: MarkProps) {
  return (
    <Mark {...props}>
      <path d="M2.5 8.5 6 12l7.5-8" />
    </Mark>
  );
}

/**
 * Near a limit, held, or waiting on a person.
 *
 * A triangle rather than a circle: the silhouette differs from every other mark
 * in the set even when colour is gone and the thing is 14px tall.
 */
export function MarkCaution(props: MarkProps) {
  return (
    <Mark {...props}>
      <path d="M8 2 15 14H1z" />
      <path d="M8 6.5v3.25" />
      <path d="M8 11.75v.75" />
    </Mark>
  );
}

/** Outside the limits: returned, rejected, failed. */
export function MarkOutOfTolerance(props: MarkProps) {
  return (
    <Mark {...props}>
      <path d="M3.5 3.5 12.5 12.5" />
      <path d="M12.5 3.5 3.5 12.5" />
    </Mark>
  );
}

/**
 * A reference value — the datum.
 *
 * The surveyor's benchmark: a horizontal reference line with the measured point
 * sitting on it.
 */
export function MarkDatum(props: MarkProps) {
  return (
    <Mark {...props}>
      <path d="M1.5 11h13" />
      <circle cx="8" cy="6.5" r="3" />
      <path d="M8 9.5V11" />
    </Mark>
  );
}

/**
 * Not applicable, or nothing recorded.
 *
 * The certificate's own em-dash. This is the mark that must never be mistaken
 * for a zero, so it is a rule at mid-height and nothing else.
 */
export function MarkAbsent(props: MarkProps) {
  return (
    <Mark {...props}>
      <path d="M3 8h10" />
    </Mark>
  );
}

/** A record that can no longer be edited in place. */
export function MarkLocked(props: MarkProps) {
  return (
    <Mark {...props}>
      <path d="M3.5 7.5h9v6h-9z" />
      <path d="M5.75 7.5V5.25a2.25 2.25 0 0 1 4.5 0V7.5" />
    </Mark>
  );
}

/**
 * A value a person entered.
 *
 * A filled square: the plotted point on a chart, the reading that was actually
 * taken. Filled means "observed".
 */
export function MarkEntered(props: MarkProps) {
  return (
    <Mark {...props}>
      <rect x="4.5" y="4.5" width="7" height="7" fill="currentColor" stroke="none" />
    </Mark>
  );
}

/**
 * A value the system derived.
 *
 * The same point, hollow. On a plotted chart a hollow marker is the fitted or
 * computed value and a filled one is the observation; the convention is already
 * in this audience's hands, so the interface borrows it rather than inventing a
 * badge that says "calculated".
 */
export function MarkCalculated(props: MarkProps) {
  return (
    <Mark {...props}>
      <rect x="4.75" y="4.75" width="6.5" height="6.5" />
    </Mark>
  );
}

/** A superseded or cancelled document. The struck circle of a void stamp. */
export function MarkVoid(props: MarkProps) {
  return (
    <Mark {...props}>
      <circle cx="8" cy="8" r="6" />
      <path d="M3.75 12.25 12.25 3.75" />
    </Mark>
  );
}

/** An issued document carrying a seal. */
export function MarkSeal(props: MarkProps) {
  return (
    <Mark {...props}>
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="3" />
    </Mark>
  );
}

/** Work in progress. A gauge needle part-way through its sweep. */
export function MarkInProgress(props: MarkProps) {
  return (
    <Mark {...props}>
      <path d="M2 12a6 6 0 1 1 12 0" />
      <path d="M8 12 11 7.5" />
    </Mark>
  );
}

/**
 * Waiting on another person: inspection, review, approval (D278). A clock
 * face — round, unlike the caution triangle, so "someone else's turn" and
 * "needs attention" stay apart without colour.
 */
export function MarkWaiting(props: MarkProps) {
  return (
    <Mark {...props}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.5V8l2.5 1.5" />
    </Mark>
  );
}
